import { inject, Injectable } from '@angular/core';
import { WizardModel } from '@configuration/wizard/models/wizard-model';
import { ProjectModelService } from '@project/service/project-model.service';
import { ConfigurationTemplate, ConfigurationTemplateStepType, ModuleAccessorySelectionOption, QuantitySelectionOption, TileSelectionOption } from '@configuration/wizard/models/wizard-model.interface';
import { ArticleWithTechnicalPropertiesDTO, TechnicalRequirementsLongVO } from '@article/properties/technical/technical-requirements';
import { AddArticleService } from '@configurator/project/add-article.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { OverlayService } from '@shared/overlay/overlay.service';
import { ConfigurationCreationService } from '@configuration/wizard/service/configuration-creation.service';
import { RouteNavigationService } from '@services/route-navigation.service';
import { CreateProjectService } from '@project/service/create-project.service';
import { ArticleDataService } from '@article/service/article-data.service';
import { lastValueFrom } from 'rxjs';
import { ProjectModel } from '@project/model/project.model';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { OpenLabelEditorService } from '@project/components/label/label-editor/service/open-label-editor.service';
import { AssistantDataService } from '@configuration/assistants/assistant-data.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { LockingPinService } from '@project/accessory/locking-pin.service';
import { ArticlePreviewUtils } from '@article/util/article-preview.utils';
import { Log } from '@shared/debug/log';
import { MountingAssistantService } from '@configuration/assistants/mounting/mounting-assistant.service';
import { PeContactService } from '@project/accessory/pe-contact.service';
import { DirectPrintingService } from '@project/components/label/direct-printing.service';
import { shareReplay } from 'rxjs/operators';
import { ArticleTechnicalInfoPropertyDef } from '@article/constants/article-technical-info-property';
import { UserUtils } from '@user/service/user-utils';
import { UserModel } from '@user/model/user.model';

@Injectable()
export class LtlWizardModel extends WizardModel {

  public readonly type = "LTL";

  // The two types of LTL articles can be discriminated by one part of their articleNo:
  // 274-24*0*1/XXXX-XXXX, 274-42*0*1/XXXX-XXXX vs 274-24*2*2/XXXX-XXXX, 274-42*2*2/XXXX-XXXX
  private readonly ltlSubTypeDiscriminator = { electricalInstallation: 0, deviceConnection: 2 };

  private readonly assistantDataService = inject(AssistantDataService);
  private readonly _assistantArticles$ =
    this.assistantDataService.getArticles('LTLAssistant', [ArticleTechnicalInfoPropertyDef.NUMBER_OF_CONNECTIONS]).pipe(shareReplay(1));

  private _fourConductorQuantity: number = 0;
  private _twoConductorQuantity: number = 0;
  private _selectedSubTypeDiscriminator: number = this.ltlSubTypeDiscriminator.electricalInstallation;

  private addLockingPins: boolean = false;
  private PEModuleSelections: boolean[] = [];

  constructor(
    private readonly overlayService: OverlayService,
    private readonly configurationCreationService: ConfigurationCreationService,
    private readonly routeNavigationService: RouteNavigationService,
    private readonly createProjectService: CreateProjectService,
    private readonly articleDataService: ArticleDataService,
    private readonly addArticleService: AddArticleService,
    private readonly projectModel: ProjectModel,
    private readonly selectionModelService: SelectionModelService,
    private readonly openLabelEditorService: OpenLabelEditorService,
    private readonly projectModelService: ProjectModelService,
    private readonly localeResourceManager: LocaleResourceManager,
    private readonly lockingPinService: LockingPinService,
    private readonly mountingAssistantService: MountingAssistantService,
    private readonly peContactService: PeContactService,
    private readonly directPrintingService: DirectPrintingService,
    private readonly userModel: UserModel,
  ) {
    super();
  }

  protected createConfig(): ConfigurationTemplate {
    const configurationTemplate: ConfigurationTemplate = {
      id: 'create-project-ltl',
      type: 'assistant',
      imageUrl: 'ng-assets/img/projecttypes/ltl.webp',
      label: 'configurationCreation.type.ltl',
      steps: [
        {
          type: "meta",
          id: "meta",
          label: "configurationCreation.progress.configuration_data",
        },
        {
          type: "tileSelection",
          id: "selectApplication",
          label: "createLtl.question.application",
          info: "createLtl.question.application.hint",
          getOptions: async(): Promise<TileSelectionOption[]> => [
            {
              label: "createLtl.option.application.240x",
              id: "application-240x",
              value: this.ltlSubTypeDiscriminator.electricalInstallation,
              imageUrl: "ng-assets/img/ltl/option_1_1.jpg",
              size: "M"
            },
            {
              label: "createLtl.option.application.242x",
              id: "application-242x",
              value: this.ltlSubTypeDiscriminator.deviceConnection,
              imageUrl: "ng-assets/img/ltl/option_1_2.jpg",
              size: "M"
            },
            ]
        },
        {
          type: "quantitySelection",
          id: "quantitySelection",
          label: 'createLtl.question.quantity',
          minTotal: 2,
          maxTotal: UserUtils.isWagoUser(this.userModel.user) ? 32 : 8,
          warning: {
            min: 2,
            max: 8,
            text: UserUtils.isWagoUser(this.userModel.user) ? 'createLtl.warning_not_between_2_and_8_modules_cost' : 'createLtl.warning_not_between_2_and_8_modules'},
          stepWidth: 2,
          getOptions: () => this.getQuantityOptions(),
        },
      ],
    };
    if (UserUtils.isWagoUser(this.userModel.user)) {
      configurationTemplate.steps.push(this.addLockingPinSelection());
    } else {
      this.addLockingPins = true;
    }
    configurationTemplate.steps.push(this.addPEModuleSelection());
    return configurationTemplate
  }

  private addLockingPinSelection(): ConfigurationTemplateStepType {
    return {
      type: "tileSelection",
      id: "selectPinSchema",
      label: "createLtl.question.locking_pins",
      getOptions: async(): Promise<TileSelectionOption[]> => [
        {
          label: "createLtl.option.locking_pins.set_per_schema",
          id: "set-locking-pins",
          value: true,
          imageUrl: "ng-assets/img/ltl/option_4_1.jpg",
          size: "L",
          disabled: !this.isAutomaticLockingPinsSetAvailable(),
        },
        {
          label: "createLtl.option.locking_pins.no_automatic_set",
          id: "no-locking-pins",
          value: false,
          imageUrl: "ng-assets/img/ltl/option_4_2.jpg",
          size: "L"
        },
      ]
    }
  }

  private addPEModuleSelection(): ConfigurationTemplateStepType {
    return {
      type: 'moduleAccessorySelection',
      id: 'selectModuleAccessories',
      label: 'createLtl.question.pe_contact_accessories',
      info: 'createLtl.info.pe_contact_accessories',
      getOptions: async (): Promise<ModuleAccessorySelectionOption[]> => this.getPEModuleSelectionOptions(),
    };
  }

  private isAutomaticLockingPinsSetAvailable(): boolean {
    const totalQuantity = this._fourConductorQuantity + this._twoConductorQuantity;
    return totalQuantity >= 2 && totalQuantity <= 8;
  }

  private async getQuantityOptions(): Promise<QuantitySelectionOption[]> {
    const sliderMax = UserUtils.isWagoUser(this.userModel.user) ? 16 : 8;
    const fourConductorArticle = await this.getSelectedArticle(4);
    const twoConductorArticle = await this.getSelectedArticle(2);
    return [
      {
        label: `<b>${fourConductorArticle.articleNo}</b> - ${this.localeResourceManager.getValueByKey('createLtl.option.4wire')}`,
        img: ArticlePreviewUtils.getPreviewImageByNo(fourConductorArticle.articleNo),
        min: 0,
        max: sliderMax,
      },
      {
        label: `<b>${twoConductorArticle.articleNo}</b> - ${this.localeResourceManager.getValueByKey('createLtl.option.2wire')}`,
        img: ArticlePreviewUtils.getPreviewImageByNo(twoConductorArticle.articleNo),
        min: 0,
        max: sliderMax,
      },
    ];
  }

  private getPEModuleSelectionOptions(): ModuleAccessorySelectionOption[] {
    const options: {label: string, image: string, modules: string[]}[] = [
      { label: this.localeResourceManager.getValueByKey('createLtl.option.4wire'), image: 'ng-assets/img/ltl/4conductor_pe.webp', modules: [] },
      { label: this.localeResourceManager.getValueByKey('createLtl.option.2wire'), image: 'ng-assets/img/ltl/2conductor_pe.webp', modules: [] },
    ];
    let moduleIndex = 0;

    for (let i = 0; i < this._fourConductorQuantity; i++) {
      options[0].modules.push(moduleIndex.toString());
      moduleIndex++;
    }
    for (let i = 0; i < this._twoConductorQuantity; i++) {
      options[1].modules.push(moduleIndex.toString());
      moduleIndex++;
    }

    return options;
  }

  private get isSimpleProject(): boolean {
    if (this._fourConductorQuantity > 0 && this._twoConductorQuantity > 0) {
      return false;
    }
    return this._fourConductorQuantity + this._twoConductorQuantity <= 8;
  }

  public async create(): Promise<void> {
    if (this.creationStarted) {
      return;
    }

    this.creationStarted = true;

    await this.routeNavigationService.toConfigurator();

    await this.createFreeMountingConfiguration();
    this.projectModel.currentProjectType = 'LTL';
    await this.addArticlesAndFinishProject();

    if (this.isSimpleProject) {
      const numberOfConnections = this._fourConductorQuantity > 0 ? 4 : 2;
      const numberOfPoles = Math.max(this._fourConductorQuantity, this._twoConductorQuantity);
      let foundCatalogArticles: ArticleWithTechnicalPropertiesDTO[] = [];
      await this.overlayService.doWithOverlay(async () => {
        foundCatalogArticles = await lastValueFrom(this.assistantDataService.searchArticles(
          'LTLAssistant', [
            new TechnicalRequirementsLongVO('NUMBER_OF_CONNECTIONS', numberOfConnections),
            new TechnicalRequirementsLongVO('NO_OF_POLES', numberOfPoles),
          ], []));
      }, OverlayBackground.color);
      if (foundCatalogArticles.length > 0) {
        this.mountingAssistantService.start('ltl-catalogue', {catalogue: foundCatalogArticles});
        return;
      }
    }
  }

  private async addArticlesAndFinishProject(showLabelEditor: boolean = true): Promise<void> {

    const fourConductorArticle = await this.getSelectedArticle(4);
    const twoConductorArticle = await this.getSelectedArticle(2);

    await this.overlayService.doWithOverlay(async () => {
      await this.addArticlesToConfiguration();
      await this.projectModelService.updateBaseArticle();
      if (showLabelEditor) {
        const articleUsageIds: string[] = (this.projectModel.currentProject?.compositions[0].articleUsages || [] as ArticleUsageVO[])
          .filter((usage => usage.articleId === fourConductorArticle.articleId || usage.articleId === twoConductorArticle.articleId))
          .map((a) => a.id);
        this.selectionModelService.selectArticlesByUsageIds(articleUsageIds);
        await this.openLabelEditorService.openForCurrentSelection();
      }
      this.projectModel.currentProjectType = 'LTL';
      await this.directPrintingService.activateUniformDirectPrinting();
      this.creationStarted = false;
    }, OverlayBackground.color);
  }

  private async createFreeMountingConfiguration(): Promise<void> {
    await this.configurationCreationService.prepareNewConfiguration(
      this.metaData,
    );
    await this.createProjectService.createFreeMounting();
  }

  private async addArticlesToConfiguration(): Promise<string[]> {

    const fourConductorArticle = await this.getSelectedArticle(4);
    const twoConductorArticle = await this.getSelectedArticle(2);

    const articleIds = [fourConductorArticle.articleId, twoConductorArticle.articleId]
      .filter((id): id is number => id !== undefined);

    const articles = await lastValueFrom(this.articleDataService.getLatestArticles(articleIds));

    const articlesById = new Map(articles.map((article) => [article.identity, article]));
    const articleIdsByPosition: number[] = [
      ...new Array(this._fourConductorQuantity).fill(fourConductorArticle.articleId),
      ...new Array(this._twoConductorQuantity).fill(twoConductorArticle.articleId),
    ].filter((articleId): articleId is number => articleId !== undefined);

    const articlesToAdd: ArticleVO[] = articleIdsByPosition
      .map((articleId) => articlesById.get(articleId))
      .filter((article): article is ArticleVO => article !== undefined);

    const articleGuids = await this.addArticleService.addArticlesToMountingRail(articlesToAdd);
    // Locking pins must be added first: adding a PE contact accessory replaces the module article,
    // which invalidates the article GUIDs the locking pin logic relies on.
    if (this.addLockingPins) {
      await this.lockingPinService.addLockingPinsToArticles(articleGuids);
    }
    const selectedModules: string[] = [];
    for (let i = 0; i < this.PEModuleSelections.length; i++) {
      if (this.PEModuleSelections[i]) {
        selectedModules.push(articleGuids[i]);
      }
    }
    if (selectedModules.length !== 0) {
      await this.peContactService.addPeAccessoryToArticles(selectedModules);
    }

    return articleGuids;
  }

  public selectValue(step: number, value: any): void {
    const stepType = this.config.steps[step]?.type;

    if (stepType === 'tileSelection' && step === 1) {
      // This is explicitely called without await, we don't need to wait for it, we just need to make sure
      // the selection methods are run in the correct order.
      this._selectedSubTypeDiscriminator = value;
      return;
    }

    if (stepType === 'quantitySelection') {
      this._fourConductorQuantity = value[0];
      this._twoConductorQuantity = value[1];
      this.PEModuleSelections = [];
      return;
    }

    if (stepType === 'moduleAccessorySelection') {
      this.PEModuleSelections = value as boolean[];
      return;
    }

    if (stepType === 'tileSelection') {
      this.addLockingPins = value;
    }
  }

  private getArticleByType(articles: ArticleWithTechnicalPropertiesDTO[], typeDiscriminator: number): ArticleWithTechnicalPropertiesDTO {
    const articleOfWantedType = articles.find((article) => {
      const discriminatingNumber: number = this.getDiscriminatingNumberFromArticleNo(article.articleNo);
      return discriminatingNumber === typeDiscriminator;
    });
    // This case can not happen, if nobody deletes one of the LTL subseries, it's just for the compiler's happiness
    if (Log.assertExists(articleOfWantedType, `No article found for type ${typeDiscriminator}`)) {
      return articleOfWantedType;
    }
    return articles[typeDiscriminator / 2];
  }

  private getDiscriminatingNumberFromArticleNo(articleNo: string): number {
    // The two types of LTL articles can be discriminated by one part of their articleNo:
    // 274-24*0*1/XXXX-XXXX, 274-42*0*1/XXXX-XXXX vs 274-24*2*2/XXXX-XXXX, 274-42*2*2/XXXX-XXXX
    // First split the articleNo  by the "/" and use only the first part
    const mainArticleNoPart = articleNo.split('/')[0];
    // Then split the first part by "-" and use only the second part
    const secondPart = mainArticleNoPart.split('-')[1];
    // Finally, use the 3rd place of the remaining number parse it as an integer and return it
    return Number.parseInt(secondPart[2]);
  }

  private async getSelectedArticle(numberOfConnections: number): Promise<ArticleWithTechnicalPropertiesDTO> {
    const assistantArticles = await lastValueFrom(this._assistantArticles$);
    const filteredArticles = assistantArticles
      .filter((article) => article.properties[ArticleTechnicalInfoPropertyDef.NUMBER_OF_CONNECTIONS]?.value === numberOfConnections);
    const conductorArticle = this.getArticleByType(filteredArticles, this._selectedSubTypeDiscriminator);
    return conductorArticle;
  }
}
